import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardText,
  CCol,
  CContainer,
  CRow,
  CSpinner,
} from '@coreui/react'
import apiClient from '../../services/apiClient'
import { useSwipeable } from 'react-swipeable'
import { buildApiUrl } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import RecommendationCard, { RecommendationSkeleton } from './components/RecommendationCard'
import InternalAppHeader from './components/InternalAppHeader'

const RisksNotificationShared = () => {
  const [searchParams] = useSearchParams()
  const grp_ID = searchParams.get('grp_ID')
  const reminder_ID = searchParams.get('reminder_ID')
  console.log('Shared Risks notification grp_ID ' + grp_ID)
  console.log('Shared Risks notification reminder_ID ' + reminder_ID)

  const [recommendations, setRecommendations] = useState([])
  const [query] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const navigate = useNavigate()
  const fetchControllerRef = useRef(null)
  const handlers = useSwipeable({
    onSwipedLeft: () => console.log('Swiped Left!'),
    onSwipedRight: () => console.log('Swiped Right!'),
    onSwipedUp: () => console.log('Swiped Up!'),
    onSwipedDown: () => console.log('Swiped Down!'),
  })

  const fetchRecommendationsData = useCallback(async () => {
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort()
    }

    const abortController = new AbortController()
    fetchControllerRef.current = abortController
    setLoading(true)

    try {
      setError(null)
      const mail = localStorage.getItem('loginName1') || ''
      if (!mail) {
        console.warn('Missing mail for shared reminders fetch.')
        setRecommendations([])
        return
      }

      const response = await apiClient.get(buildApiUrl('remindersShared', { mail }), {
        signal: abortController.signal,
      })
      const data = Array.isArray(response.data) ? response.data : []
      setRecommendations(data)
    } catch (fetchError) {
      if (fetchError.name === 'CanceledError' || fetchError.name === 'AbortError') {
        return
      }
      console.error('Error fetching recommendations', fetchError)
      setError('Unable to load shared activities. Please try again.')
    } finally {
      if (fetchControllerRef.current === abortController) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchRecommendationsData()

    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort()
      }
    }
  }, [fetchRecommendationsData, searchParams])

  useEffect(() => {
    SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
      console.warn('Unable to hide splash screen on mount', splashError)
    })
  }, [])

  useEffect(() => {
    if (!loading) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
        console.warn('Unable to hide splash screen', splashError)
      })
    }
  }, [loading])

  const filteredRecommendations = useMemo(() => {
    if (!Array.isArray(recommendations) || recommendations.length === 0) {
      return []
    }
    return recommendations.filter((recommendation) =>
      recommendation.Title.toLowerCase().includes(query.toLowerCase()),
    )
  }, [recommendations, query])

  const selectedRecommendation = useMemo(() => {
    if (filteredRecommendations.length === 0) {
      return null
    }

    if (!reminder_ID) {
      return filteredRecommendations[0]
    }

    return (
      filteredRecommendations.find(
        (recommendation) => String(recommendation.reminder_ID) === String(reminder_ID),
      ) || filteredRecommendations[0]
    )
  }, [filteredRecommendations, reminder_ID])

  const normalizedRecommendation = useMemo(() => {
    if (!selectedRecommendation) return null
    return {
      ...selectedRecommendation,
      pic_duplicate: selectedRecommendation.pic_duplicate || selectedRecommendation.pic,
    }
  }, [selectedRecommendation])

  const selectReminder = useCallback((recommendation) => {
    if (!recommendation) return
    localStorage.setItem('taskid', recommendation.reminder_ID)
  }, [])

  const handleDetailNavigation = useCallback(
    (recommendation) => {
      if (!recommendation) return
      console.log('Task Detail ' + recommendation.reminder_ID)
      selectReminder(recommendation)
      navigate('/detail')
    },
    [navigate, selectReminder],
  )

  const postponeSharedRecommendation = useCallback(
    async (recommendation) => {
      if (!recommendation) return false
      const reminderId = recommendation.reminder_ID
      const accountId = recommendation.account_ID
      const expectedDate = localStorage.getItem('AImonth') || ''

      if (!reminderId || !accountId) {
        console.warn('Missing identifiers for shared reminder postpone.')
        return false
      }

      try {
        await apiClient.get(
          buildApiUrl('remindersDueDateUpdate', {
            reminder_ID: reminderId,
            expecteddate: expectedDate,
            account_ID: accountId,
          }),
        )
        await fetchRecommendationsData()
        return true
      } catch (postponeError) {
        console.error('Error postponing shared reminder', postponeError)
        setError('Unable to postpone reminder. Please try again.')
        return false
      }
    },
    [fetchRecommendationsData],
  )

  const completeSharedRecommendation = useCallback(
    async (recommendation) => {
      if (!recommendation) return false
      const reminderId = recommendation.reminder_ID
      const accountId = recommendation.account_ID

      if (!reminderId || !accountId) {
        console.warn('Missing identifiers for completing shared reminder.')
        return false
      }

      try {
        await apiClient.get(
          buildApiUrl('remindersStatusUpdate', {
            reminder_ID: reminderId,
            state: 'D',
            account_ID: accountId,
          }),
        )
        await apiClient.get(
          buildApiUrl('accountsTransferContactTaskUpdate', {
            account_ID: accountId,
            reminder_ID: reminderId,
            contact: '',
          }),
        )
        await fetchRecommendationsData()
        return true
      } catch (completeError) {
        console.error('Error completing shared reminder', completeError)
        setError('Unable to complete reminder. Please try again.')
        return false
      }
    },
    [fetchRecommendationsData],
  )

  const handleDoneAction = useCallback(
    async (recommendation) => {
      if (!recommendation) return

      selectReminder(recommendation)
      console.log('Task Done')
      console.log('Task Detail ' + recommendation.reminder_ID)

      const today = new Date()
      let nextMonth = today.getMonth() + 2
      if (nextMonth > 12) {
        nextMonth -= 12
      }
      localStorage.setItem('AImonth', String(nextMonth))

      const postponed = await postponeSharedRecommendation(recommendation)
      if (!postponed) {
        return false
      }

      const completed = await completeSharedRecommendation(recommendation)
      if (!completed) {
        return false
      }

      return true
    },
    [completeSharedRecommendation, postponeSharedRecommendation, selectReminder],
  )

  const handleDoneActionWrapper = useCallback(
    async (recommendation) => {
      const handled = await handleDoneAction(recommendation)
      if (!handled) {
        console.warn('Shared reminder completion aborted or failed.')
        return
      }
      navigate('/dashboard', { replace: true })
    },
    [handleDoneAction, navigate],
  )

  const isInitialLoading = loading && recommendations.length === 0 && !error

  const handleNavigateBack = useCallback(() => {
    console.log('Back')
    window.history.back()
  }, [])

  if (isInitialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <CSpinner color="primary" variant="grow" style={{ width: '3rem', height: '3rem' }} />
      </div>
    )
  }

  return (
    <div className="risk-notification-wrapper min-vh-100 d-flex flex-column">
      <CContainer>
        <InternalAppHeader title="Shared actions" onBack={handleNavigateBack} />
        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="risk-notification-card border-0 shadow-sm" {...handlers}>
              <CCardBody>
                {error && (
                  <CAlert
                    color="danger"
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>{error}</span>
                    <CButton
                      color="danger"
                      variant="outline"
                      size="sm"
                      onClick={fetchRecommendationsData}
                    >
                      Retry
                    </CButton>
                  </CAlert>
                )}
                <div className="risk-notification-card__body">
                  {loading && !normalizedRecommendation ? (
                    <RecommendationSkeleton />
                  ) : normalizedRecommendation ? (
                    <RecommendationCard
                      recommendation={normalizedRecommendation}
                      onDetail={handleDetailNavigation}
                      onSupplier={undefined}
                      onEshop={undefined}
                      onCalendar={undefined}
                      onSwitchOff={undefined}
                      onIgnore={undefined}
                      onDone={handleDoneActionWrapper}
                      onInnovationSuppliers={undefined}
                      onInnovationStatus={undefined}
                    />
                  ) : (
                    <CCardText className="alert alert-info mb-0">
                      There is nothing more to do right now.
                    </CCardText>
                  )}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default RisksNotificationShared
